import { Component, EventEmitter, Input, OnChanges, Output, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { IActivityLog, IAlarmRouteChangeRecord, AlarmRouteChangeRecordField, VISIBLE_DETAILS_FIELDS_COUNT, Device } from 'app/models/activity-log.model';
import { RouterModule } from '@angular/router';
import { IdentityFormatPipe, UserRolePipe, JoinPipe, IncludesPipe, MomentPipe, SomePipe, DayOfWeekPipe } from 'app/pipes/pipes';
import { CallCenterDeliveryType } from 'app/models/alarmroutes.model';


@Component({
    selector: 'app-activity-alarm-route-details',
    imports: [
        CommonModule,
        TranslateModule,
        RouterModule,
        IdentityFormatPipe,
        IncludesPipe,
        MomentPipe,
        SomePipe,
        DayOfWeekPipe
    ],
    templateUrl: './activity-alarm-route-details.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    styleUrls: ['../activity-details-dialog/activity-details-content-styles.css']
})
export class ActivityAlarmRouteDetailsComponent implements OnChanges, OnInit {
  @Output() closeDialog = new EventEmitter<void>();
  @Input() log: IActivityLog;
  actData: any[] = [];
  constructor(private translate: TranslateService) { } 

  readonly visibleFieldCount = VISIBLE_DETAILS_FIELDS_COUNT;

  callCenterDeliveryTypeTranslationsMap: Map<CallCenterDeliveryType, string> = new Map([
    [CallCenterDeliveryType.UnableToDeliverAlarm, 'UNABLE_TO_DELIVER_ALARM'],
    [CallCenterDeliveryType.CallCenterFirst, 'CALL_CENTER_FIRST'],
    [CallCenterDeliveryType.CallCenterOnly, 'CALL_CENTER_ONLY'],
    [CallCenterDeliveryType.WhenUnableToDeliverEMACallCenterOnly, 'UNABLE_TO_DELIVER_EMA_CALL_CENTER_ONLY'],
    [CallCenterDeliveryType.WhenUnableToDeliverEMAAlarm, 'UNABLE_TO_DELIVER_EMA_ALARM'],
  ]);

  alarmRouteChanges: IAlarmRouteChangeRecord;
  visibleFields: AlarmRouteChangeRecordField[] = [];
  oldCallCenterDeliveryType: string;
  newCallCenterDeliveryType: string;
  visibleCount = 15; // Initially show 15 items
  oldCallCenterName: string;  
  newCallCenterName: string;

  ngOnInit() {
    this.actData = JSON.parse(localStorage.getItem('act') || '[]');

    // Define the list of device types that should map to the special alert
    const deviceTypes = [
      "04-1", "04-2", "04-3", "04-4",
      "05-3", "05-4", "05-5", "05-7", "05-9",
      "06-2", "09-1",
      "34-1", "34-2", "34-3"
    ];

    const group = 81;
    const node = 22;
    const correctAlertName = "NO_CONNECTION_WITHIN_TIME";

    // Generate all special mappings based on the device types
    const specialMappings = deviceTypes.map((deviceType) => ({
      deviceType,
      group,
      node,
      name: correctAlertName
    }));

    // Remove any incorrect entries (same deviceType, group, and node)
    this.actData = this.actData.filter(
      (item) =>
        !specialMappings.some(
        (special) =>
          special.deviceType === item.deviceType &&
          special.group === item.group &&
          special.node === item.node
      )
    );

    // Add special mappings
    this.actData = [...this.actData, ...specialMappings];

  }

  getVisibleItems(added: any[], removed: any[]) {
    const addedItems = added || [];
    const removedItems = removed || [];

    const totalCount = addedItems.length + removedItems.length;
    const newVisibleCount = Math.min(this.visibleCount, totalCount);

    const visibleAdded = Math.min(addedItems.length, newVisibleCount);
    const visibleRemoved = newVisibleCount - visibleAdded;

    const hiddenCount = totalCount - newVisibleCount;

    return { visibleAdded, visibleRemoved, hiddenCount };
  }

  ngOnChanges(): void {
    this.alarmRouteChanges = this.log?.details?.alarmRouteChanges;
    const result = this.getVisibleFields(this.alarmRouteChanges);
    this.visibleFields = [...result.visibleFields];

    if (this.alarmRouteChanges?.fallbackCallCenter?.newValue == "VERIFI") {
      this.alarmRouteChanges.fallbackCallCenter.newValue = "EMAAC";
    }
    else if (this.alarmRouteChanges?.fallbackCallCenter?.oldValue == "VERIFI") {
      this.alarmRouteChanges.fallbackCallCenter.oldValue = "EMAAC";
    }
  }

  private getVisibleFields(alarmRouteChanges: IAlarmRouteChangeRecord): { visibleFields: AlarmRouteChangeRecordField[] } {
    if (!alarmRouteChanges) {
      return { visibleFields: [] };
    }
    const visibleFields = Object.keys(alarmRouteChanges).filter(obj => obj) as AlarmRouteChangeRecordField[];
  
      const hasDeliveryTypeChange =
          alarmRouteChanges.callCenterFirst?.newValue !== alarmRouteChanges.callCenterFirst?.oldValue ||
          alarmRouteChanges.keepAlarmOpenAfterCallCenter?.newValue !== alarmRouteChanges.keepAlarmOpenAfterCallCenter?.oldValue ||
          alarmRouteChanges.emaBeforeCallCenter?.newValue !== alarmRouteChanges.emaBeforeCallCenter?.oldValue;
          
      // True when a call center is added to a route that previously had none
      const isNewCallCenter = alarmRouteChanges.isNewCallCenter ?? false;

      // Show delivery type only if a new call center was added or delivery settings changed
      if (isNewCallCenter || hasDeliveryTypeChange) {
          if (!visibleFields.includes('callCenterDeliveryType')) visibleFields.push('callCenterDeliveryType');

          const oldCallCenterName = alarmRouteChanges.callCenterName?.oldValue?.trim() || null;
          const newCallCenterName = alarmRouteChanges.callCenterName?.newValue?.trim() || null;
          if (isNewCallCenter) {
              this.oldCallCenterDeliveryType = 'NONE';
              this.oldCallCenterName = 'NONE';
          } else {
              const oldDeliveryType = this.getDeliveryType(
                  alarmRouteChanges.callCenterFirst?.oldValue ?? false,
                  alarmRouteChanges.keepAlarmOpenAfterCallCenter?.oldValue ?? false,
                  alarmRouteChanges.emaBeforeCallCenter?.oldValue ?? true
              );
              this.oldCallCenterDeliveryType = this.callCenterDeliveryTypeTranslationsMap.get(oldDeliveryType) ?? 'UNABLE_TO_DELIVER_ALARM';
              this.oldCallCenterName = oldCallCenterName ?? 'NONE';
          }

          const newDeliveryType = this.getDeliveryType(
              alarmRouteChanges.callCenterFirst?.newValue ?? true,
              alarmRouteChanges.keepAlarmOpenAfterCallCenter?.newValue ?? true,
              alarmRouteChanges.emaBeforeCallCenter?.newValue ?? false
          );
          this.newCallCenterDeliveryType = this.callCenterDeliveryTypeTranslationsMap.get(newDeliveryType) ?? 'UNABLE_TO_DELIVER_ALARM';
          this.newCallCenterName = newCallCenterName ?? 'NONE';
    }
    return { visibleFields };
  }

  private getDeliveryType(callCenterFirst: boolean, keepAlarmOpenAfterCallCenter: boolean, emaBeforeCallCenter: boolean): CallCenterDeliveryType | undefined {
    if (!callCenterFirst && !keepAlarmOpenAfterCallCenter && !emaBeforeCallCenter) {
      return CallCenterDeliveryType.UnableToDeliverAlarm;
    }
    if (callCenterFirst && keepAlarmOpenAfterCallCenter && !emaBeforeCallCenter) {
      return CallCenterDeliveryType.CallCenterFirst;
    }
    if (callCenterFirst && !keepAlarmOpenAfterCallCenter && !emaBeforeCallCenter) {
      return CallCenterDeliveryType.CallCenterOnly;
    }
    if (callCenterFirst && !keepAlarmOpenAfterCallCenter && emaBeforeCallCenter) {
      return CallCenterDeliveryType.WhenUnableToDeliverEMACallCenterOnly;
    }
    if (callCenterFirst && keepAlarmOpenAfterCallCenter && emaBeforeCallCenter) {
      return CallCenterDeliveryType.WhenUnableToDeliverEMAAlarm;
    }
    return undefined;
  }

  getOrderedSchedules(alarmRouteChanges: IAlarmRouteChangeRecord) {
    if (!alarmRouteChanges) return [];

    const weekOrder = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

    const added = alarmRouteChanges.addedServiceTimes || [];
    const removed = alarmRouteChanges.removedServiceTimes || [];

    // Group schedules by day
    const groupedSchedules = weekOrder.map((day) => {
      return {
        day,
        removed: removed.filter(s => s.day === day),
        added: added.filter(s => s.day === day),
      };
    });

    return groupedSchedules;
  }

  getTranslatedAlertTypes(device: Device): string {
    return this.getAlertTypes(device)
      .map(alert => this.translate.instant(alert))
      .join(', ');
  }

  getAlertTypes(device: Device): string[] {
    if (typeof device === 'string') {
      return [device];
    }

    // Find the matching alert object based on devType, group, and node
    const alerts = this.actData
      .filter((item) =>
        device.alertTypes.some(
          (alert) =>
            alert.devType === item.deviceType &&
            alert.group === item.group &&
            alert.node === item.node
        )
      )
      .map((alert) => alert.name);

    // Return the alert type name, or a default value if not found
    return alerts.length ? alerts : [];
  }

}
